import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { formatValue, getUnit } from '../utils/formatValue';
import {
  colorText,
  colorTextAdditional,
} from '../../../utils/palette';
import { getCategoryColour } from '../utils/categoryColours';
import {
  repairReasonKeyMap,
  repairClassKeyMap,
  repairSiteKeyMap,
} from '../../../shared/constants/index';
import RankedBarChart from './RankedBarChart';
import TimeSeriesChart from './TimeSeriesChart';
import PieChart from './PieChart';

const LABEL_MAP_KEYS = {
  repairReason: repairReasonKeyMap,
  repairClass:  repairClassKeyMap,
  repairSite:   repairSiteKeyMap,
};

const DOMAIN_LABEL = {
  fuel:        'domain_fuel',
  utilization: 'domain_utilization',
  tco:         'domain_tco',
  oc:          'domain_oc',
  inspections: 'domain_inspections',
  workorders:  'domain_workorders',
};

// ── Card shell ─────────────────────────────────────────────────────────────────

const Card = styled.div`
  grid-column: span ${({ $colSpan }) => $colSpan};
  background: #fff;
  border: 1px solid #E7EAF0;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
`;

const CardHeader = styled.div`
  flex-shrink: 0;
`;

const DomainRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
`;

const CategoryDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $colour }) => $colour};
  flex-shrink: 0;
`;

const DomainLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: ${colorTextAdditional};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CardTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${colorText};
`;

const CardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

// ── Coming soon placeholder ────────────────────────────────────────────────────

const PlaceholderBody = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px dashed #E7EAF0;
  border-radius: 8px;
  font-size: 12px;
  color: ${colorTextAdditional};
`;

const NoDataBody = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: ${colorTextAdditional};
  min-height: 60px;
`;

// ── Skeleton shimmer ───────────────────────────────────────────────────────────

const shimmer = keyframes`
  0%   { background-position: 200% center; }
  100% { background-position: -200% center; }
`;

const SkeletonBody = styled.div`
  flex: 1;
  min-height: 120px;
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    #E7EAF0 25%,
    #f5f6f8 50%,
    #E7EAF0 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease infinite;
`;

// ── Component ─────────────────────────────────────────────────────────────────

export default function MetricWidget({ config, data, loading }) {
  const { t } = useTranslation('common');
  const storeUnits = useSelector((state) => state.units);
  const colSpan = config?.colSpan ?? 1;
  const domainLabel = t(DOMAIN_LABEL[config?.domain] ?? config?.domain ?? '');
  const title = config?.labelKey ? t(config.labelKey, { defaultValue: config?.label ?? '' }) : (config?.label ?? '');
  const chartType = data?.chartType ?? 'coming_soon';
  const valueFormat = config?.valueFormat;
  const colour = getCategoryColour(config?.domain);

  const formatFn = useMemo(
    () => (value) => formatValue(value, valueFormat, storeUnits),
    [valueFormat, storeUnits],
  );

  const unitLabel = useMemo(
    () => getUnit(valueFormat, storeUnits),
    [valueFormat, storeUnits],
  );

  const labelMap = useMemo(() => {
    const keyMap = LABEL_MAP_KEYS[config?.labelMapKey];
    if (!keyMap) return null;
    return Object.fromEntries(
      Object.entries(keyMap).map(([code, i18nKey]) => [Number(code), t(i18nKey)])
    );
  }, [config?.labelMapKey, t]);

  return (
    <Card $colSpan={colSpan}>
      <CardHeader>
        <DomainRow>
          <CategoryDot $colour={colour} />
          <DomainLabel>{domainLabel}</DomainLabel>
        </DomainRow>
        <CardTitle>{title}</CardTitle>
      </CardHeader>

      <CardBody>
        {loading ? (
          <SkeletonBody />
        ) : (
          <>
            {chartType === 'rankedBar'   && <RankedBarChart data={data} formatFn={formatFn} colour={colour} />}
            {chartType === 'timeSeries'  && <TimeSeriesChart data={data} formatFn={formatFn} unitLabel={unitLabel} colour={colour} />}
            {chartType === 'pie'         && <PieChart data={data} labelMap={labelMap} />}
            {chartType === 'coming_soon' && (
              <PlaceholderBody>{t('coming_soon')}</PlaceholderBody>
            )}
            {chartType === 'no_data' && (
              <NoDataBody>{t('no_data_found')}</NoDataBody>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}
